import { Briefcase, Clock, ShieldCheck, TriangleAlert, UserPlus, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { HrBarList } from '../components/BarList';
import { HrColumnChart } from '../components/ColumnChart';
import { HrKpiGrid } from '../components/KpiGrid';
import {
  CORE_TICKETS,
  countBy,
  coverageTone,
  daysBetween,
  filterRequisitions,
  holdsWhitecard,
  isPipelineCandidate,
  isSiteLevel,
  last12Months,
  percent,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrSectionProps } from './types';
import { HrStatusPill } from '../components/StatusPill';

export function OverviewSection({
  data,
  people,
  filters,
  asOf,
  toggleDept,
}: HrSectionProps) {
  const ids = new Set(people.map((person) => person.id));
  const qualifications = data.qualifications.filter((row) => ids.has(row.personId));
  const signoffs = data.policySignoffs.filter((row) => ids.has(row.userId));
  const requisitions = filterRequisitions(data.requisitions, filters);
  const requisitionIds = new Set(requisitions.map((row) => row.id));
  const candidates = data.candidates.filter((row) => requisitionIds.has(row.req));

  const starters90 = people.filter((person) => {
    const days = daysBetween(person.start, asOf);
    return days !== null && days >= 0 && days <= 90;
  });

  const withStart = people.filter((person) => person.tenure !== null);
  const underOneYear = withStart.filter((person) => (person.tenure as number) < 1);

  const openRequisitions = requisitions.filter(
    (row) => row.status === 'IN_PROGRESS' || row.status === 'OPEN'
  );
  const pipeline = candidates.filter(isPipelineCandidate);

  const completedSignoffs = signoffs.filter((row) => row.status === 'Completed').length;
  const signoffRate = percent(completedSignoffs, signoffs.length);

  const expiring90 = qualifications.filter(
    (row) => row.days !== null && row.days >= 0 && row.days <= 90
  );
  const expired = qualifications.filter((row) => row.days !== null && row.days < 0);

  const months = last12Months(asOf);
  const startersByMonth = months.map((month) => {
    const value = people.filter((person) => person.start?.startsWith(month.key)).length;
    return { label: month.label, full: month.full, value };
  });

  const twelveMonthsAgo = new Date(`${asOf.slice(0, 10)}T00:00:00`);
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  const recentStarters = people.filter((person) => {
    if (!person.start) return false;
    const start = new Date(`${person.start}T00:00:00`);
    return start >= twelveMonthsAgo;
  });
  const startersByDept = sortCountsDesc(countBy(recentStarters, (person) => person.dept)).map(
    ([label, value]) => ({
      key: label,
      label,
      value,
      hint: `${value} started in the last 12 months`,
    })
  );

  const nonCasual = people.filter((person) => person.emp !== 'Casual');
  const siteStaff = people.filter((person) => isSiteLevel(person.level));
  const whitecardHolders = siteStaff.filter((person) =>
    holdsWhitecard(data.qualifications, person.id)
  ).length;

  const activePolicies = data.policies.filter((policy) => policy.status === 'Active');
  const assignedActivePolicies = activePolicies.filter((policy) => policy.assigned > 0).length;

  const completeness = [
    {
      key: 'Headcount',
      label: 'Headcount',
      value: percent(
        people.filter((person) => person.dept && person.role).length,
        people.length
      ),
      hint: 'Department and job role on every record',
    },
    {
      key: 'Demographics',
      label: 'Demographics',
      value: percent(people.filter((person) => person.age !== null).length, people.length),
      hint: 'Date of birth on file',
    },
    {
      key: 'Diversity',
      label: 'Diversity',
      value: percent(
        people.filter(
          (person) =>
            person.gender &&
            person.indigenousStatus &&
            person.countryOfBirth &&
            person.disabilityStatus
        ).length,
        people.length
      ),
      hint: 'All four demographic fields recorded',
    },
    {
      key: 'Recruitment',
      label: 'Recruitment',
      value: 100,
      hint: 'Requisitions and candidates are captured',
    },
    {
      key: 'Leave',
      label: 'Leave',
      value: percent(
        nonCasual.filter((person) => person.lb_annual !== null).length,
        nonCasual.length
      ),
      hint: 'Non-casual staff with a balance',
    },
    {
      key: 'Qualifications',
      label: 'Qualifications',
      value: percent(whitecardHolders, siteStaff.length),
      hint: 'Site staff with a Whitecard on record',
    },
    {
      key: 'Policies',
      label: 'Policies',
      value: percent(assignedActivePolicies, activePolicies.length),
      hint: 'Active policies assigned to staff',
    },
    {
      key: 'Learning',
      label: 'Learning',
      value: data.courseAssignments.length > 0 ? 100 : 0,
      hint: 'LMS course assignments',
    },
  ];

  const headcountByDept = sortCountsDesc(countBy(people, (person) => person.dept)).map(
    ([label, value]) => ({
      key: label,
      label,
      value,
      hint: `${value} active staff`,
    })
  );

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Active headcount',
            value: people.length,
            detail: `${people.filter((person) => person.status === 'Onboarding').length} onboarding`,
            icon: <Users className="h-5 w-5" />,
          },
          {
            label: 'New starters · 90 days',
            value: starters90.length,
            detail: 'By start date',
            icon: <UserPlus className="h-5 w-5" />,
          },
          {
            label: 'Under 1 year service',
            value: underOneYear.length,
            detail: `Of ${withStart.length} with a start date`,
            icon: <Clock className="h-5 w-5" />,
          },
          {
            label: 'Open requisitions',
            value: openRequisitions.length,
            detail: `${pipeline.length} candidates in pipeline`,
            icon: <Briefcase className="h-5 w-5" />,
          },
          {
            label: 'Policy sign-off',
            value: signoffRate,
            unit: '%',
            detail: `${completedSignoffs} of ${signoffs.length} assigned`,
            tone: signoffRate < 60 ? 'danger' : 'success',
            icon: <ShieldCheck className="h-5 w-5" />,
          },
          {
            label: 'Tickets expiring ≤ 90d',
            value: expiring90.length,
            detail: `${expired.length} already expired`,
            tone: expiring90.length > 0 ? 'warning' : 'success',
            icon: <TriangleAlert className="h-5 w-5" />,
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Starters by month</CardTitle>
            <CardDescription>
              Current employees by start month · last 12 months
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HrColumnChart data={startersByMonth} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">New starters by department</CardTitle>
            <CardDescription>
              Started in the last 12 months · select a bar to filter every page
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList
              items={startersByDept}
              onSelect={toggleDept}
              selectedKeys={filters.dept}
              formatter={(value) => String(value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Data completeness by section</CardTitle>
            <CardDescription>
              How much of each page is backed by data in Idarah
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList
              items={completeness.map((item) => ({
                key: item.key,
                label: item.label,
                value: item.value,
                hint: item.hint,
                color: `var(--${coverageTone(item.value) === 'success' ? 'success' : coverageTone(item.value) === 'warning' ? 'warning' : 'destructive'})`,
              }))}
              max={100}
              formatter={(value) => `${value}%`}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Headcount by department</CardTitle>
            <CardDescription>Select a bar to filter every page</CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList
              items={headcountByDept}
              onSelect={toggleDept}
              selectedKeys={filters.dept}
            />
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <HrStatusPill tone="link">Live data</HrStatusPill>
        <span>Core tickets matched by name: {CORE_TICKETS.length} categories.</span>
      </div>
    </div>
  );
}
